import { useEffect, useMemo, useState } from "react";
import { hoy } from "./SelectorDeDia";
import { useTranslation } from "react-i18next";

import { CentinelaAvatar } from "../../../components/base/CentinelaChat";
import Modal from "../../../components/base/Modal";
import { Badge, Tabs } from "../../../components/ui";
import type { Unit } from "../../../store/admin/unitsStore";
import { useDeviceDashCam } from "../../../store/admin/vehiclesSocketSelector";
import {
  useCentinelaCatalogs,
  useCentinelaUnitBitacora,
  useCentinelaUnitConduccion,
  useCentinelaUnitMedia,
  useCentinelaConfig,
  useCentinelaUnitCalls,
  useCentinelaUnitConfig,
  useCentinelaUnitEvents,
} from "../../../features/centinela/queries";
import {
  CAMERA_LOG_KINDS,
  mergeCameraEvents,
  toCameraEvents,
  toCameraLogEvents,
} from "../services/dashcamEvents";
import { formatSpeed, formatUpdatedAgo } from "../services/formatWatchTime";
import { countCallsToday, mapCentinelaCalls } from "../services/mapCentinelaCall";
import {
  CENTINELA_COMPANY_BEHAVIORS,
  CENTINELA_UNIT_BEHAVIORS,
  CENTINELA_UNSOURCED_BEHAVIORS,
  deriveUnitBehaviors,
} from "../services/mapCentinelaConfig";
import type { BehaviorState } from "../services/mapCentinelaConfig";
import {
  AI_MESSAGE_ICONS,
  AI_MESSAGE_TONES,
  CHIP_TONE_CLASSES,
  WATCH_STATUS_CARD,
  WATCH_STATUS_ICONS,
  WATCH_STATUS_TONES,
} from "../theme";
import type { SentinelBehaviorId, UnitWatchState } from "../types";
import CaseEventsPanel from "./CaseEventsPanel";
import TabLoader from "./TabLoader";
import ConversationPanel from "./ConversationPanel";
import DashcamPanel from "./DashcamPanel";
import ConduccionPanel from "./ConduccionPanel";
import EvidenciasPanel from "./EvidenciasPanel";

const BEHAVIOR_DOT_CLASSES = {
  on: "bg-success",
  off: "bg-content-muted",
  unknown: "border border-content-muted",
} as const satisfies Record<BehaviorState, string>;

const BEHAVIOR_LABEL_CLASSES = {
  on: "text-content-secondary",
  off: "text-content-faint line-through",
  unknown: "text-content-faint",
} as const satisfies Record<BehaviorState, string>;

interface BehaviorRow {
  id: SentinelBehaviorId;
  state: BehaviorState;
}

function BehaviorRows({ rows }: { rows: BehaviorRow[] }) {
  const { t } = useTranslation("common");

  return (
    <ul className="space-y-2">
      {rows.map(({ id, state }) => (
        <li key={id} className="flex items-center gap-2.5">
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${BEHAVIOR_DOT_CLASSES[state]}`}
          />
          <span className={`min-w-0 break-words text-xs ${BEHAVIOR_LABEL_CLASSES[state]}`}>
            {t(`monitoreoIa.config.behaviors.${id}.label`)}
          </span>
        </li>
      ))}
    </ul>
  );
}

export interface UnitWatchModalProps {
  unit: Unit;
  watch: UnitWatchState;
  deviceId: string | null;
  onClose: () => void;
}

export default function UnitWatchModal({
  unit,
  watch,
  deviceId,
  onClose,
}: UnitWatchModalProps) {
  const { t } = useTranslation("common");
  const [activeTab, setActiveTab] = useState("overview");

  const { haveDashCam, dashCam, imei } = useDeviceDashCam(deviceId);

  // El parte del día. NO depende de la cámara: una unidad con GPS a secas trae
  // movimientos, incidencias y llamadas, y esa es justo de la que menos se sabe.
  // "" = hoy. El día se guarda aquí y no dentro del panel porque el query
  // vive en este modal, como el resto de las pestañas.
  const [fechaParte, setFechaParte] = useState("");
  // Sin `recorrido`: la traza del día son miles de puntos y aquí no se dibuja
  // el mapa. Ese mismo hook, pidiéndola, es el que va a alimentar la pestaña
  // de mapa cuando se le pidan los eventos de conducción.
  const parteQuery = useCentinelaUnitConduccion(deviceId, { fecha: fechaParte });

  const callsQuery = useCentinelaUnitCalls(deviceId);
  const eventsQuery = useCentinelaUnitEvents(deviceId);
  const catalogsQuery = useCentinelaCatalogs();
  // La evidencia del CASO ya no se pide aquí: este modal no la dibuja —vive en
  // la tarjeta del caso, que es donde se atiende— y pedirla hacía esperar al
  // usuario por algo que nunca iba a ver. Una consulta menos por cada vez que
  // se abre la ficha de una unidad.
  // La otra mitad de lo que ve la cámara: nt-31 y nt-32 son de solo registro,
  // así que no existen como casos y sin esto no se verían nunca.
  /*
    ── El día que se está mirando en Evidencias ──────────────────────────────
    Arranca en hoy, como el parte de conducción. Antes eran 24 horas fijas
    contadas hacia atrás desde ahora, y el efecto era que cada día que pasaba
    se caía un día de evidencia de la pantalla aunque siguiera guardada: la
    cámara de la Cherokee subió 166 archivos un jueves y el viernes por la
    tarde la pestaña decía «no ha subido nada», porque todos habían quedado
    fuera de la ventana.
  */
  const [fechaEvidencia, setFechaEvidencia] = useState(hoy);
  const unitMediaQuery = useCentinelaUnitMedia(deviceId, { fecha: fechaEvidencia });
  const bitacoraQuery = useCentinelaUnitBitacora(deviceId, {
    horas: 24,
    tipos: CAMERA_LOG_KINDS.join(","),
  });

  const configQuery = useCentinelaConfig();
  const unitConfigQuery = useCentinelaUnitConfig(deviceId);

  const behaviors = useMemo(
    () => deriveUnitBehaviors(configQuery.data, unitConfigQuery.data),
    [configQuery.data, unitConfigQuery.data],
  );

  const unitBehaviorRows = useMemo<BehaviorRow[]>(
    () => CENTINELA_UNIT_BEHAVIORS.map((id) => ({ id, state: behaviors[id] })),
    [behaviors],
  );

  const companyBehaviorRows = useMemo<BehaviorRow[]>(
    () => CENTINELA_COMPANY_BEHAVIORS.map((id) => ({ id, state: behaviors[id] })),
    [behaviors],
  );

  const unsourcedBehaviorRows = useMemo<BehaviorRow[]>(
    () => CENTINELA_UNSOURCED_BEHAVIORS.map((id) => ({ id, state: "unknown" })),
    [],
  );

  const hasUnknownBehavior = [...unitBehaviorRows, ...companyBehaviorRows].some(
    (row) => row.state === "unknown",
  );

  const behaviorsLoading = configQuery.isLoading || unitConfigQuery.isLoading;

  const calls = useMemo(() => mapCentinelaCalls(callsQuery.data?.calls), [callsQuery.data]);
  const events = useMemo(() => eventsQuery.data?.events ?? [], [eventsQuery.data]);

  // Un caso solo viaja con el id de su tipo (`nt-28`); la etiqueta legible vive
  // en el catálogo servido, que es quien la actualiza cuando el catálogo cambia.
  const alertLabels = useMemo(
    () =>
      Object.fromEntries(
        (catalogsQuery.data?.alert_types ?? []).map((alert) => [alert.id, alert.label]),
      ),
    [catalogsQuery.data],
  );

  const cameraEvents = useMemo(
    () =>
      mergeCameraEvents(
        toCameraEvents(events, alertLabels),
        toCameraLogEvents(bitacoraQuery.data?.entradas ?? []),
      ),
    [events, alertLabels, bitacoraQuery.data],
  );

  const driverName = unit.driver?.name ?? t("monitoreoIa.units.noDriver");
  const operatorName = driverName.split(" ")[0];
  const style = WATCH_STATUS_CARD[watch.status];
  const lowFuel = watch.fuelPercent < 20;
  const aiMessageTone = AI_MESSAGE_TONES[watch.aiMessageType];
  const hasAiMessage = watch.aiMessage.trim().length > 0;

  const updatedLabel = formatUpdatedAgo(watch.updatedAt, t);

  useEffect(() => {
    if (!haveDashCam && activeTab === "dashcam") setActiveTab("overview");
  }, [haveDashCam, activeTab]);

  const tabs = [
    { id: "overview", label: t("monitoreoIa.units.tabs.overview"), icon: "ri-information-line" },
    ...(haveDashCam
      ? [
          {
            id: "dashcam",
            label: t("monitoreoIa.units.tabs.dashcam"),
            icon: "ri-dashboard-3-line",
          },
          // Condicional a propósito, al revés que Conducción: sin cámara esta
          // pestaña estaría siempre vacía y sería una promesa incumplida en la
          // barra. Conducción sigue teniendo kilómetros, tiempos y hechos.
          {
            id: "evidencias",
            label: t("monitoreoIa.units.tabs.evidencias", { defaultValue: "Evidencias" }),
            icon: "ri-image-2-line",
          },
        ]
      : []),
    {
      id: "conduccion",
      label: t("monitoreoIa.units.tabs.conduccion", { defaultValue: "Conducción" }),
      icon: "ri-steering-2-line",
    },
    {
      id: "calls",
      label: t("monitoreoIa.units.tabs.calls"),
      icon: "ri-message-3-line",
      count: calls.length,
      countLoading: callsQuery.isLoading,
    },
    {
      id: "protocols",
      label: t("monitoreoIa.units.tabs.protocols"),
      icon: "ri-file-list-3-line",
      count: events.length,
      countLoading: eventsQuery.isLoading,
    },
  ];

  const facts = [
    {
      id: "driver",
      label: t("monitoreoIa.units.driver"),
      value: driverName,
    },
    {
      id: "location",
      label: t("monitoreoIa.units.location"),
      value: watch.address ?? unit.lastAddress ?? t("monitoreoIa.units.noLocation"),
    },
    {
      id: "speed",
      label: t("monitoreoIa.units.speed"),
      value: formatSpeed(watch.speedKph, t),
    },
    {
      id: "phone",
      label: t("monitoreoIa.units.operatorPhone"),
      value: watch.hasOperatorPhone
        ? t("monitoreoIa.units.operatorPhoneSet")
        : t("monitoreoIa.units.operatorPhoneMissing"),
    },
    {
      id: "calls",
      label: t("monitoreoIa.units.callsToday"),
      value: callsQuery.isLoading ? "…" : `${countCallsToday(calls)}`,
    },
  ];

  return (
    <Modal
      title={unit.name}
      onClose={onClose}
      width="w-full max-w-4xl"
      content={
        /*
          ── Por qué la tarjeta mide siempre lo mismo ─────────────────────────
          Antes crecía con el contenido y la ventana entera se estiraba: al
          cambiar de pestaña el modal daba un brinco, las pestañas se iban
          hacia arriba y para volver a cambiar de pestaña había que subir el
          scroll. Ahora la altura la fija la pantalla —no el contenido— y lo
          único que se desplaza es el cuerpo: el encabezado y las pestañas
          quedan siempre a la vista, en el mismo lugar.

          78vh más el alto del encabezado del modal deja margen bajo el
          `max-h-[95vh]` de la tarjeta, así que su scroll nunca se activa y no
          hay dos barras compitiendo.
        */
        <div className="flex h-[78dvh] min-w-0 flex-col gap-4">
          <div className="shrink-0 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={WATCH_STATUS_TONES[watch.status]}>
                <i className={WATCH_STATUS_ICONS[watch.status]} />
                {t(`monitoreoIa.status.${watch.status}`)}
              </Badge>
              <span className="min-w-0 max-w-full truncate text-xs text-content-secondary">
                {driverName} · {unit.plate ?? t("monitoreoIa.units.noPlate")}
              </span>
            </div>

            <Tabs items={tabs} activeId={activeTab} onChange={setActiveTab} />
          </div>

          {/* `min-h-0` no es decorativo: sin él un hijo flex se niega a
              encogerse por debajo de su contenido y el scroll nunca aparece. */}
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden pr-1">
          {activeTab === "overview" && (
            <div className="space-y-4">
              <div className={`rounded-xl border p-4 ${style.border} ${style.header}`}>
                <div className="mb-3 flex items-center gap-3">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${style.icon}`}
                  >
                    <i className="ri-truck-line text-lg" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-bold text-content">{unit.name}</p>
                    <p className="text-[11px] text-content-faint">
                      {unit.plate ?? t("monitoreoIa.units.noPlate")}
                    </p>
                  </div>
                </div>

                <dl className="grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2">
                  {facts.map((fact) => (
                    <div key={fact.id} className="rounded-lg border border-line bg-card p-3">
                      <dt className="text-[10px] uppercase tracking-wide text-content-faint">
                        {fact.label}
                      </dt>
                      <dd className="mt-0.5 truncate text-[13px] font-semibold text-content">
                        {fact.value}
                      </dd>
                    </div>
                  ))}

                  <div className="rounded-lg border border-line bg-card p-3 sm:col-span-2">
                    <dt className="text-[10px] uppercase tracking-wide text-content-faint">
                      {t("monitoreoIa.units.fuel")}
                    </dt>
                    <dd className="mt-1.5 flex items-center gap-2">
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-subtle">
                        <span
                          className={`block h-full rounded-full transition-all ${
                            lowFuel ? "bg-error" : "bg-info"
                          }`}
                          style={{ width: `${watch.fuelPercent}%` }}
                        />
                      </span>
                      <span
                        className={`text-xs font-bold tabular-nums ${
                          lowFuel ? "text-error" : "text-info"
                        }`}
                      >
                        {watch.fuelPercent}%
                      </span>
                    </dd>
                  </div>
                </dl>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {watch.chips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${CHIP_TONE_CLASSES[chip.tone]}`}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>

              <div
                className={`rounded-xl border p-4 ${
                  hasAiMessage && aiMessageTone !== "neutral"
                    ? CHIP_TONE_CLASSES[aiMessageTone]
                    : "border-line bg-surface"
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <CentinelaAvatar size="sm" className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wide text-content-faint">
                      {t("monitoreoIa.assistantName")}
                    </p>
                    <p
                      className={`mt-1 break-words text-[12.5px] leading-relaxed ${
                        hasAiMessage ? "" : "italic text-content-faint"
                      }`}
                    >
                      {hasAiMessage && aiMessageTone !== "neutral" && (
                        <i
                          className={`${AI_MESSAGE_ICONS[watch.aiMessageType]} mr-1 align-[-0.1em] text-xs`}
                        />
                      )}
                      {hasAiMessage ? watch.aiMessage : t("monitoreoIa.units.noAiMessage")}
                    </p>
                  </div>
                </div>
              </div>


              <div className="rounded-xl border border-line bg-surface p-4">
                <div className="mb-3 flex items-center gap-2">
                  <i className="ri-settings-3-line text-sm text-primary-500" />
                  <span className="text-[11px] font-bold uppercase tracking-wide text-content-secondary">
                    {t("monitoreoIa.units.activeBehaviors")}
                  </span>
                </div>

                {behaviorsLoading ? (
                  <TabLoader label={t("monitoreoIa.units.behaviorsLoading")} />
                ) : (
                  <>
                    <p className="mb-2 text-[10px] uppercase tracking-wide text-content-faint">
                      {t("monitoreoIa.units.behaviorsUnitScope")}
                    </p>

                    <BehaviorRows rows={unitBehaviorRows} />

                    <p className="mb-2 mt-3 border-t border-line pt-3 text-[10px] uppercase tracking-wide text-content-faint">
                      {t("monitoreoIa.units.behaviorsCompanyScope")}
                    </p>

                    <BehaviorRows rows={companyBehaviorRows} />

                    {unsourcedBehaviorRows.length > 0 && (
                      <>
                        <p className="mb-2 mt-3 border-t border-line pt-3 text-[10px] uppercase tracking-wide text-content-faint">
                          {t("monitoreoIa.units.behaviorsUnknown")}
                        </p>

                        <BehaviorRows rows={unsourcedBehaviorRows} />
                      </>
                    )}

                    {hasUnknownBehavior && (
                      <p className="mt-3 text-[10px] leading-relaxed text-content-faint">
                        {t("monitoreoIa.units.behaviorsUnknownHint")}
                      </p>
                    )}
                  </>
                )}
              </div>

              <p className="text-center text-[10px] text-content-faint">
                {t("monitoreoIa.units.lastUpdate", { time: updatedLabel })}
              </p>
            </div>
          )}


          {activeTab === "calls" &&
            (callsQuery.isLoading ? (
              <TabLoader />
            ) : (
              <ConversationPanel calls={calls} operatorName={operatorName} />
            ))}

          {activeTab === "protocols" &&
            (eventsQuery.isLoading ? (
              <TabLoader />
            ) : (
              <CaseEventsPanel events={events} catalogLabels={alertLabels} />
            ))}

          {activeTab === "conduccion" && (
            <ConduccionPanel
              parte={parteQuery.data?.conduccion ?? null}
              pareja={parteQuery.data?.pareja ?? null}
              fecha={parteQuery.data?.fecha ?? ""}
              seleccion={fechaParte}
              onFecha={setFechaParte}
              isLoading={parteQuery.isLoading}
              isFetching={parteQuery.isFetching}
              tieneCamara={haveDashCam}
              cameraEvents={cameraEvents}
            />
          )}

          {haveDashCam && activeTab === "evidencias" && (
            <EvidenciasPanel
              deviceId={deviceId}
              unitMedia={unitMediaQuery.data?.media ?? []}
              unitMediaLoading={unitMediaQuery.isLoading}
              dia={fechaEvidencia}
              onDia={setFechaEvidencia}
              ultima={unitMediaQuery.data?.ultima ?? null}
              openCaseId={watch.openCaseId}
            />
          )}

          {haveDashCam && activeTab === "dashcam" && (
            <DashcamPanel
              updatedAt={watch.updatedAt}
              dashCam={dashCam}
              deviceId={deviceId ?? undefined}
              imei={imei}
              unitName={unit.name}
              unitPlate={unit.plate}
              driverName={driverName}
            />
          )}
          </div>
        </div>
      }
    />
  );
}
